/**
 * WizardSidebar
 *
 * Left sidebar for the onboarding wizard.
 * Shows the TimeTrackPro logo, onboarding setup label,
 * step indicators, and a Resume Onboarding button pinned to bottom.
 */

import { Check } from 'lucide-react';
import { useOnboarding, type OnboardingStep } from '@/contexts/OnboardingContext';
import { Button } from '@/components/ui/button';

export interface StepDefinition {
  number: number;       // internal OnboardingStep value (used for active/completed matching)
  displayNumber: number; // sequential display number shown in the badge (1, 2, 3, 4, 5)
  label: string;
  description: string;
}

interface WizardSidebarProps {
  currentStep: OnboardingStep;
  steps: StepDefinition[];
  /** Whether a given step (1-indexed) has been completed */
  isStepCompleted: (stepNumber: number) => boolean;
}

const PRIMARY = '#00796B';

export function WizardSidebar({ currentStep, steps, isStepCompleted }: WizardSidebarProps) {
  const { saveStep, openWizard } = useOnboarding();

  const handleResumeClick = () => {
    // If wizard is already open this button acts as a home button back to welcome
    saveStep(0);
    openWizard();
  };

  return (
    <div
      className="flex flex-col h-full w-[280px] shrink-0"
      style={{ backgroundColor: '#E0F2F1' }}
    >
      {/* Logo + App name */}
      <div className="px-4 pt-4 pb-3">
        <div className="flex items-center gap-3 mb-5">
          {/* Access Time Logo mark */}
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-base shrink-0"
            style={{ backgroundColor: PRIMARY }}
          >
            T
          </div>
          <span className="font-semibold text-lg text-gray-900">Access Time</span>
        </div>

        {/* Section label */}
        <div
          className="text-xs font-semibold tracking-widest uppercase"
          style={{ color: '#546E7A' }}
        >
          Onboarding Setup
        </div>
      </div>

      {/* Step indicators — scrollable if needed */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        <div className="space-y-1">
          {steps.map((step) => {
            const isActive = currentStep === step.number;
            const isCompleted = isStepCompleted(step.number);
            const isUpcoming = !isActive && !isCompleted;

            return (
              <div key={step.number} className="flex items-start gap-3 p-2 rounded-lg">
                {/* Badge */}
                <div className="shrink-0 mt-0.5">
                  {isCompleted ? (
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: PRIMARY }}
                    >
                      <Check className="w-4 h-4 text-white" strokeWidth={2.5} />
                    </div>
                  ) : isActive ? (
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold"
                      style={{
                        backgroundColor: `${PRIMARY}20`,
                        color: PRIMARY,
                        boxShadow: `0 0 0 2px ${PRIMARY}`,
                      }}
                    >
                      {step.displayNumber}
                    </div>
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-500">
                      {step.displayNumber}
                    </div>
                  )}
                </div>

                {/* Step info */}
                <div className="min-w-0">
                  <div
                    className="text-sm font-semibold leading-tight"
                    style={{ color: isActive ? PRIMARY : isCompleted ? '#1a1a1a' : '#6B7280' }}
                  >
                    {step.label}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 leading-tight">
                    {step.description}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Resume Onboarding button pinned to bottom */}
      <div className="border-t border-teal-200 px-4 py-4">
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-left justify-start text-sm"
          style={{ color: PRIMARY }}
          onClick={handleResumeClick}
        >
          ↩ Resume from beginning
        </Button>
      </div>
    </div>
  );
}
